import { useState } from 'react';
import { FiX, FiCheckCircle, FiAlertCircle, FiDollarSign, FiRefreshCw } from 'react-icons/fi';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';

const CHANNELS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'pos',           label: 'POS' },
];

const generateReference = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const random = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return `MAN-${random}`;
};

const toLocalDatetimeValue = (date) => {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const inputClass = "w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 placeholder-gray-400 transition-all";
const selectClass = "w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 transition-all";

const RecordOfflinePaymentModal = ({ closeModal, payment, onSuccess }) => {
  const paymentId = payment?._id;
  const studentName = `${payment?.child?.firstname || ''} ${payment?.child?.lastname || ''}`.trim();
  const totalAmount = Number(payment?.total_amount || 0);
  const alreadyPaid = Number(payment?.amount_paid || 0);
  const outstanding = Math.max(0, totalAmount - alreadyPaid);

  const [displayAmount, setDisplayAmount] = useState('');
  const [rawAmount, setRawAmount] = useState('');
  const [channel, setChannel] = useState('cash');
  const [reference, setReference] = useState(generateReference);
  const [paidAt, setPaidAt] = useState(toLocalDatetimeValue(new Date()));
  const [narration, setNarration] = useState('');
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });

  const handleAmountChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setRawAmount(digits);
    setDisplayAmount(digits ? Number(digits).toLocaleString('en-NG') : '');
  };

  const handleSave = async () => {
    if (!rawAmount || Number(rawAmount) <= 0) {
      setAlert({ type: 'error', message: 'Please enter a valid amount.' });
      return;
    }
    if (!paymentId) return;
    setSaving(true);
    setAlert({ type: '', message: '' });
    try {
      await userRequest.post(`/payments/${paymentId}/transaction`, {
        amount: Number(rawAmount),
        channel,
        reference,
        paid_at: new Date(paidAt).toISOString(),
        narration: narration || undefined,
      });
      setAlert({ type: 'success', message: 'Payment recorded successfully.' });
      setTimeout(() => { onSuccess?.(); closeModal(); }, 1500);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to record payment. Please try again.';
      setAlert({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-xl">
              <FiDollarSign className="text-green-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Record Payment</h2>
              <p className="text-xs text-gray-400 capitalize">{studentName}</p>
            </div>
          </div>
          <button onClick={closeModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">

          {alert.message && (
            <div className={`flex items-start gap-3 rounded-xl px-4 py-3 text-sm border ${alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {alert.type === 'success' ? <FiCheckCircle className="mt-0.5 shrink-0" /> : <FiAlertCircle className="mt-0.5 shrink-0" />}
              <span>{alert.message}</span>
            </div>
          )}

          {/* Outstanding summary */}
          <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 space-y-1.5 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Fee</span>
              <span className="font-medium text-gray-800">{formatCurrency(totalAmount)}</span>
            </div>
            {alreadyPaid > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-500">Already Paid</span>
                <span className="text-green-600 font-medium">−{formatCurrency(alreadyPaid)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1.5 border-t border-gray-200 font-semibold">
              <span className="text-gray-700">Outstanding</span>
              <span className="text-red-500">{formatCurrency(outstanding)}</span>
            </div>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Amount</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">₦</span>
              <input
                type="text"
                inputMode="numeric"
                value={displayAmount}
                onChange={handleAmountChange}
                placeholder="0"
                className="w-full pl-8 pr-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-400 placeholder-gray-400"
                autoFocus
              />
            </div>
          </div>

          {/* Channel */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Payment Channel</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className={selectClass}>
              {CHANNELS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Reference */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Reference</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setReference(generateReference())}
                title="Regenerate"
                className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors shrink-0"
              >
                <FiRefreshCw size={15} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Date paid */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Date Paid</label>
            <input
              type="datetime-local"
              value={paidAt}
              onChange={(e) => setPaidAt(e.target.value)}
              className={inputClass}
            />
          </div>

          {/* Narration */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Narration <span className="normal-case font-normal text-gray-400">(optional)</span></label>
            <input
              type="text"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              placeholder="e.g. January fees payment"
              className={inputClass}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 shrink-0">
          <button type="button" onClick={closeModal} disabled={saving}
            className="px-5 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !rawAmount}
            className="px-5 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full transition-colors disabled:bg-green-300">
            {saving ? 'Saving...' : 'Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecordOfflinePaymentModal;
